import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterEntity } from '_entities';
import { FILTER_OPTIONS } from '_constants/old/filters';
import { BaseSelect } from 'ui-kit';
import * as Styled from './styles';

const { setShadowFilter } = FilterEntity.actions;

const PLACE_GROUPS = [
  { label: 'Schools', value: 'schools', options: FILTER_OPTIONS.NEAR_SCHOOL_PROXIMITY_OPTIONS },
  { label: 'Parks', value: 'parks', options: FILTER_OPTIONS.NEAR_PARKS_PROXIMITY_OPTIONS },
  {
    label: 'Schools or Parks',
    value: 'schools_or_parks',
    options: FILTER_OPTIONS.NEAR_PARKS_OR_SCHOOLS_PROXIMITY_OPTIONS,
  },
  {
    label: 'Schools and Parks',
    value: 'schools_and_parks',
    options: FILTER_OPTIONS.NEAR_PARKS_AND_SCHOOLS_PROXIMITY_OPTIONS,
  },
];

const RETAILER_OPTIONS = FILTER_OPTIONS.NEAR_RETAILER_PROXIMITY_OPTIONS;

const DISTANCE_LABELS = ['500 ft', '1,000 ft', '1,500 ft', '2,000 ft', '1/2 mile', '1 mile'];
const DEFAULT_DISTANCE_INDEX = 1;
const DEFAULT_PLACE_VALUE = 'schools';

const ALL_PROXIMITY_FIELDS = [
  ...new Set(
    [...PLACE_GROUPS.flatMap((g) => g.options), ...RETAILER_OPTIONS].flatMap((o) =>
      [o.field, o.legacyField].filter(Boolean),
    ),
  ),
];

const findActiveSelection = (shadowFilters) => {
  const retailerIndex = RETAILER_OPTIONS.findIndex((opt) => shadowFilters[opt.field] === (opt.value ?? 't'));
  if (retailerIndex !== -1) {
    return { type: 'retailer', distanceIndex: retailerIndex, isLegacy: false };
  }

  let placeResult = null;
  PLACE_GROUPS.some((group) =>
    group.options.some((opt, i) => {
      if (shadowFilters[opt.field] === (opt.value ?? 't')) {
        placeResult = { type: 'place', placeValue: group.value, distanceIndex: i, isLegacy: false };
        return true;
      }
      if (opt.legacyField && shadowFilters[opt.legacyField] === 't') {
        placeResult = { type: 'place', placeValue: group.value, distanceIndex: i, isLegacy: true };
        return true;
      }
      return false;
    }),
  );

  return placeResult ?? { type: null };
};

const ProximityFilterSection = () => {
  const { shadowFilters } = useSelector((state) => state.oldFilter);
  const dispatch = useDispatch();

  const active = useMemo(() => findActiveSelection(shadowFilters), [shadowFilters]);

  const [placeValue, setPlaceValue] = useState(active.type === 'place' ? active.placeValue : DEFAULT_PLACE_VALUE);
  const [placeDistance, setPlaceDistance] = useState(
    active.type === 'place' ? active.distanceIndex : DEFAULT_DISTANCE_INDEX,
  );
  const [retailerDistance, setRetailerDistance] = useState(
    active.type === 'retailer' ? active.distanceIndex : DEFAULT_DISTANCE_INDEX,
  );

  useEffect(() => {
    if (active.type === 'place') {
      setPlaceValue(active.placeValue);
      setPlaceDistance(active.distanceIndex);
    } else if (active.type === 'retailer') {
      setRetailerDistance(active.distanceIndex);
    }
  }, [active.type, active.placeValue, active.distanceIndex]);

  const distanceOptions = useMemo(() => DISTANCE_LABELS.map((label, i) => ({ label, value: i })), []);
  const placeSelectOptions = useMemo(() => PLACE_GROUPS.map((g) => ({ label: g.label, value: g.value })), []);

  const dispatchProximity = (selection) => {
    const updated = { ...shadowFilters };
    ALL_PROXIMITY_FIELDS.forEach((f) => {
      delete updated[f];
    });
    if (selection?.type === 'place') {
      const group = PLACE_GROUPS.find((g) => g.value === selection.placeValue);
      const opt = group.options[selection.distanceIndex];
      updated[opt.field] = opt.value ?? 't';
    } else if (selection?.type === 'retailer') {
      const opt = RETAILER_OPTIONS[selection.distanceIndex];
      updated[opt.field] = opt.value ?? 't';
    }
    dispatch(setShadowFilter(updated));
  };

  useEffect(() => {
    if (active.isLegacy && active.type === 'place') {
      dispatchProximity({
        type: 'place',
        placeValue: active.placeValue,
        distanceIndex: active.distanceIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlaceRadioChange = () => {
    dispatchProximity({ type: 'place', placeValue, distanceIndex: placeDistance });
  };
  const handleRetailerRadioChange = () => {
    dispatchProximity({ type: 'retailer', distanceIndex: retailerDistance });
  };
  const handlePlaceDistanceChange = (option) => {
    setPlaceDistance(option.value);
    if (active.type === 'place') {
      dispatchProximity({ type: 'place', placeValue, distanceIndex: option.value });
    }
  };
  const handlePlaceValueChange = (option) => {
    setPlaceValue(option.value);
    if (active.type === 'place') {
      dispatchProximity({ type: 'place', placeValue: option.value, distanceIndex: placeDistance });
    }
  };
  const handleRetailerDistanceChange = (option) => {
    setRetailerDistance(option.value);
    if (active.type === 'retailer') {
      dispatchProximity({ type: 'retailer', distanceIndex: option.value });
    }
  };

  return (
    <Styled.Wrapper>
      <Styled.Row>
        <Styled.RadioInput
          type="radio"
          name="proximity-filter"
          id="proximity-place-radio"
          checked={active.type === 'place'}
          onChange={handlePlaceRadioChange}
        />
        <Styled.RowLabel htmlFor="proximity-place-radio">Retailers within</Styled.RowLabel>
        <Styled.SelectWrapper>
          <BaseSelect
            inputId="proximity-place-distance"
            options={distanceOptions}
            value={distanceOptions[placeDistance]}
            onChange={handlePlaceDistanceChange}
            menuPlacement="bottom"
          />
        </Styled.SelectWrapper>
        <Styled.InlineText>of:</Styled.InlineText>
        <Styled.SelectWrapper $wide>
          <BaseSelect
            inputId="proximity-place-value"
            options={placeSelectOptions}
            value={placeSelectOptions.find((o) => o.value === placeValue)}
            onChange={handlePlaceValueChange}
            menuPlacement="bottom"
          />
        </Styled.SelectWrapper>
      </Styled.Row>

      <Styled.Row>
        <Styled.RadioInput
          type="radio"
          name="proximity-filter"
          id="proximity-retailer-radio"
          checked={active.type === 'retailer'}
          onChange={handleRetailerRadioChange}
        />
        <Styled.RowLabel htmlFor="proximity-retailer-radio">Retailers within</Styled.RowLabel>
        <Styled.SelectWrapper>
          <BaseSelect
            inputId="proximity-retailer-distance"
            options={distanceOptions}
            value={distanceOptions[retailerDistance]}
            onChange={handleRetailerDistanceChange}
            menuPlacement="bottom"
          />
        </Styled.SelectWrapper>
        <Styled.InlineText>of another retailer</Styled.InlineText>
      </Styled.Row>
    </Styled.Wrapper>
  );
};

export default ProximityFilterSection;
